<template>
  <IntercomLayout>
    <!-- Topbar -->
    <template #topbar>
      <GymTopbar
        :business-name="businessName"
        :user="user"
        :global-user="globalUser"
        :search-fn="globalSearch"
        @select-member="handleSelectMember"
        @logout="handleLogout"
      />
    </template>

    <!-- Sidebar -->
    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <!-- Main Content -->
    <div class="member-detail-view">
      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <IntercomSpinner />
      </div>

      <!-- Member Detail -->
      <template v-else-if="member">
        <!-- Page Header -->
        <div class="page-header">
          <div class="header-left">
            <IntercomButton variant="ghost" size="sm" @click="$router.push('/members')">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              <span>{{ $t('common.back') }}</span>
            </IntercomButton>
            <MemberAvatar
              :photo-url="member.photo_url"
              :name="member.name"
              size="lg"
            />
            <div class="header-info">
              <h1 class="page-title">{{ member.name }}</h1>
              <span class="member-number">{{ member.member_number }}</span>
            </div>
          </div>
          <div class="header-actions">
            <IntercomButton
              variant="ghost"
              :class="{ 'danger-ghost': member.is_active }"
              @click="showStatusModal = true"
            >
              <svg v-if="member.is_active" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                <line x1="9" y1="10" x2="15" y2="10" />
              </svg>
              <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span>{{ member.is_active ? $t('memberDetail.markInactive') : $t('memberDetail.reactivate') }}</span>
            </IntercomButton>
            <IntercomButton variant="secondary" @click="$router.push(`/members/${member.id}/edit`)">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              <span>{{ $t('memberDetail.editMember') }}</span>
            </IntercomButton>
            <IntercomButton variant="primary" @click="showRenewalModal = true">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M23 4v6h-6" />
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
              </svg>
              <span>{{ $t('memberDetail.renewMembership') }}</span>
            </IntercomButton>
          </div>
        </div>

        <!-- Member Info Cards -->
        <div class="info-grid">
          <!-- Member Information -->
          <IntercomCard class="info-card">
            <h3 class="card-title">{{ $t('memberDetail.memberInfo') }}</h3>
            <div class="info-list">
              <div class="info-row">
                <span class="info-label">{{ $t('members.phone') }}</span>
                <span class="info-value">{{ member.phone }}</span>
              </div>
              <div v-if="member.email" class="info-row">
                <span class="info-label">{{ $t('members.email') }}</span>
                <span class="info-value">{{ member.email }}</span>
              </div>
              <div v-if="member.date_of_birth" class="info-row">
                <span class="info-label">{{ $t('memberForm.dateOfBirth') }}</span>
                <span class="info-value">{{ formatDate(member.date_of_birth) }}</span>
              </div>
              <div v-if="member.gender" class="info-row">
                <span class="info-label">{{ $t('memberForm.gender') }}</span>
                <span class="info-value">{{ $t(`memberForm.gender${capitalize(member.gender)}`) }}</span>
              </div>
              <div v-if="member.address" class="info-row">
                <span class="info-label">{{ $t('memberForm.address') }}</span>
                <span class="info-value">{{ member.address }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">{{ $t('memberDetail.memberSince') }}</span>
                <span class="info-value">{{ formatDate(member.created_at) }}</span>
              </div>
            </div>
          </IntercomCard>

          <!-- Membership Information -->
          <IntercomCard class="info-card">
            <h3 class="card-title">{{ $t('memberDetail.membershipInfo') }}</h3>
            <div class="membership-status">
              <span class="status-badge" :class="getMemberStatus(member)">
                {{ $t(`common.${getMemberStatus(member)}`) }}
              </span>
            </div>
            <div class="info-list">
              <div class="info-row">
                <span class="info-label">{{ $t('memberDetail.currentPlan') }}</span>
                <span class="info-value">{{ member.plan_name || $t('memberDetail.noPlan') }}</span>
              </div>
              <div v-if="member.membership_start" class="info-row">
                <span class="info-label">{{ $t('memberForm.enrollWithPlan') }}</span>
                <span class="info-value">{{ formatDate(member.membership_start) }}</span>
              </div>
              <div v-if="member.membership_end" class="info-row">
                <span class="info-label">{{ $t('members.membershipEnd') }}</span>
                <span class="info-value expiry" :class="getExpiryClass(member.membership_end)">
                  {{ formatDate(member.membership_end) }}
                  ({{ formatExpiryDays(member.membership_end) }})
                </span>
              </div>
            </div>
          </IntercomCard>

          <!-- Emergency Contact -->
          <IntercomCard v-if="member.emergency_contact || member.emergency_phone" class="info-card">
            <h3 class="card-title">{{ $t('memberForm.emergencyContact') }}</h3>
            <div class="info-list">
              <div v-if="member.emergency_contact" class="info-row">
                <span class="info-label">{{ $t('memberForm.emergencyName') }}</span>
                <span class="info-value">{{ member.emergency_contact }}</span>
              </div>
              <div v-if="member.emergency_phone" class="info-row">
                <span class="info-label">{{ $t('memberForm.emergencyPhone') }}</span>
                <span class="info-value">{{ member.emergency_phone }}</span>
              </div>
            </div>
          </IntercomCard>

          <!-- Physical Information -->
          <IntercomCard v-if="member.height_cm || member.weight_kg || member.has_exercise_experience !== undefined" class="info-card">
            <h3 class="card-title">{{ $t('memberForm.physicalInfo') }}</h3>
            <div class="info-list">
              <div class="info-row">
                <span class="info-label">{{ $t('memberForm.previousExperience') }}</span>
                <span class="info-value">{{ member.has_exercise_experience ? $t('common.yes') : $t('common.no') }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">{{ $t('memberForm.height') }}</span>
                <span class="info-value">{{ member.height_cm ? `${member.height_cm} cm` : '-' }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">{{ $t('memberForm.weight') }}</span>
                <span class="info-value">{{ member.weight_kg ? `${member.weight_kg} kg` : '-' }}</span>
              </div>
            </div>
          </IntercomCard>
        </div>

        <!-- Payment History -->
        <IntercomCard class="payment-history-card">
          <div class="section-header">
            <h3>{{ $t('memberDetail.paymentHistory') }}</h3>
          </div>

          <div v-if="payments.length === 0" class="empty-state">
            <p>{{ $t('memberDetail.noPayments') }}</p>
          </div>

          <table v-else class="payments-table">
            <thead>
              <tr>
                <th>{{ $t('payments.receiptNumber') }}</th>
                <th>{{ $t('payments.paymentDate') }}</th>
                <th>{{ $t('members.plan') }}</th>
                <th>{{ $t('payments.amount') }}</th>
                <th>{{ $t('payments.paymentMode') }}</th>
                <th>{{ $t('payments.validity') }}</th>
                <th>{{ $t('payments.status') }}</th>
                <th>{{ $t('common.actions') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="payment in payments" :key="payment.id" :class="{ voided: payment.status === 'void' }">
                <td class="receipt-number">{{ payment.receipt_number }}</td>
                <td>{{ formatDate(payment.payment_date) }}</td>
                <td>{{ payment.plan_name }}</td>
                <td class="amount">{{ formatCurrency(payment.amount) }}</td>
                <td>{{ payment.payment_mode }}</td>
                <td>
                  {{ formatDate(payment.membership_start) }} - {{ formatDate(payment.membership_end) }}
                </td>
                <td>
                  <span class="payment-status" :class="payment.status">
                    {{ $t(`payments.status${capitalize(payment.status)}`) }}
                  </span>
                </td>
                <td class="actions">
                  <IntercomButton
                    variant="ghost"
                    size="sm"
                    @click="openPrintModal(payment)"
                  >
                    {{ $t('common.print') }}
                  </IntercomButton>
                  <IntercomButton
                    v-if="payment.status === 'active'"
                    variant="ghost"
                    size="sm"
                    @click="handleResendReceipt(payment)"
                  >
                    {{ $t('payments.resendReceipt') }}
                  </IntercomButton>
                </td>
              </tr>
            </tbody>
          </table>
        </IntercomCard>
      </template>

      <!-- Not Found -->
      <div v-else class="not-found">
        <p>{{ $t('errors.notFound') }}</p>
        <IntercomButton variant="primary" @click="$router.push('/members')">
          {{ $t('common.back') }}
        </IntercomButton>
      </div>

      <!-- Renewal Modal -->
      <IntercomModal v-model:show="showRenewalModal" :title="$t('memberDetail.renewMembership')">
        <form @submit.prevent="handleRenewal" class="renewal-form">
          <div class="form-field">
            <label>{{ $t('memberForm.selectPlan') }}</label>
            <select v-model="renewalData.plan_id" required>
              <option value="">{{ $t('memberForm.selectPlan') }}</option>
              <option v-for="plan in plans" :key="plan.id" :value="plan.id">
                {{ plan.name }} - {{ formatCurrency(plan.price) }} ({{ plan.duration_days }} days)
              </option>
            </select>
          </div>

          <div class="form-field">
            <label>{{ $t('memberForm.amount') }}</label>
            <IntercomInput v-model.number="renewalData.amount" type="number" required />
          </div>

          <div class="form-field">
            <label>{{ $t('memberForm.paymentMode') }}</label>
            <select v-model="renewalData.payment_mode" required>
              <option value="cash">{{ $t('memberForm.cash') }}</option>
              <option value="upi">{{ $t('memberForm.upi') }}</option>
              <option value="card">{{ $t('memberForm.card') }}</option>
              <option value="bank_transfer">{{ $t('memberForm.bankTransfer') }}</option>
            </select>
          </div>

          <div class="modal-actions">
            <IntercomButton variant="secondary" type="button" @click="showRenewalModal = false">
              {{ $t('common.cancel') }}
            </IntercomButton>
            <IntercomButton variant="primary" type="submit" :loading="renewalLoading">
              {{ $t('memberDetail.renewMembership') }}
            </IntercomButton>
          </div>
        </form>
      </IntercomModal>

      <!-- Status Toggle Modal -->
      <IntercomModal
        v-model:show="showStatusModal"
        :title="member?.is_active ? $t('memberDetail.markInactiveTitle') : $t('memberDetail.reactivateTitle')"
      >
        <div class="status-modal-content">
          <p class="status-modal-body">
            {{ member?.is_active
              ? $t('memberDetail.markInactiveBody', { name: member?.name })
              : $t('memberDetail.reactivateBody', { name: member?.name }) }}
          </p>
          <div class="modal-actions">
            <IntercomButton variant="secondary" type="button" @click="showStatusModal = false" :disabled="statusLoading">
              {{ $t('common.cancel') }}
            </IntercomButton>
            <IntercomButton
              :variant="member?.is_active ? 'ghost' : 'primary'"
              :class="{ 'danger-ghost': member?.is_active }"
              type="button"
              :loading="statusLoading"
              @click="handleToggleActive"
            >
              {{ member?.is_active ? $t('memberDetail.markInactive') : $t('memberDetail.reactivate') }}
            </IntercomButton>
          </div>
        </div>
      </IntercomModal>

      <!-- Print Modal -->
      <IntercomModal
        v-model:show="showPrintModal"
        :title="$t('receipt.printPreview')"
        size="lg"
      >
        <div class="print-modal-content">
          <div class="print-preview-container">
            <GymReceiptPrint
              v-if="printingPayment"
              :payment="printingPayment"
              :member="member"
              :business-name="businessName"
              :business-address="user?.business?.address"
              :business-phone="user?.business?.phone"
              :business-email="user?.business?.email"
            />
          </div>
          <div class="print-actions">
            <IntercomButton variant="primary" @click="handlePrint">
              {{ $t('common.print') }}
            </IntercomButton>
            <IntercomButton variant="ghost" @click="showPrintModal = false">
              {{ $t('common.cancel') }}
            </IntercomButton>
          </div>
        </div>
      </IntercomModal>
    </div>
  </IntercomLayout>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@shared/composables/useAuth.js'
import { useToast } from '@shared/composables/useToast.js'
import { useGym } from '../composables/useGym.js'
import { useNavigation } from '../composables/useNavigation.js'
import { IntercomLayout, IntercomSidebar, IntercomCard, IntercomButton, IntercomInput, IntercomSpinner, IntercomModal } from '@shared/components/intercom'
import GymTopbar from '../components/GymTopbar.vue'
import GymReceiptPrint from '../components/print/GymReceiptPrint.vue'
import MemberAvatar from '../components/MemberAvatar.vue'
import dayjs from 'dayjs'

const router = useRouter()
const route = useRoute()
const { t } = useI18n()
const { user, globalUser, logout } = useAuth()
const toast = useToast()
const { getMemberDetail, listPlans, createPayment, resendReceipt, updateMember, globalSearch } = useGym()

const { navSections } = useNavigation()

// State
const loading = ref(true)
const member = ref(null)
const payments = ref([])
const plans = ref([])
const showRenewalModal = ref(false)
const renewalLoading = ref(false)
const renewalData = ref({
  plan_id: '',
  amount: 0,
  payment_mode: 'cash',
})

// Print modal state
const showPrintModal = ref(false)
const printingPayment = ref(null)

// Status toggle modal state
const showStatusModal = ref(false)
const statusLoading = ref(false)

// Computed
const businessName = computed(() => user.value?.business?.name || '')

// Methods
const capitalize = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1) : ''

const formatDate = (date) => {
  if (!date) return '-'
  return dayjs(date).format('DD MMM YYYY')
}

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount)
}

const getMemberStatus = (member) => {
  if (!member.is_active) return 'inactive'
  if (!member.membership_end) return 'inactive'
  const days = dayjs(member.membership_end).diff(dayjs(), 'day')
  if (days < 0) return 'expired'
  if (days <= 7) return 'expiring'
  return 'active'
}

const formatExpiryDays = (date) => {
  if (!date) return ''
  const days = dayjs(date).diff(dayjs(), 'day')
  if (days < 0) return `${Math.abs(days)} ${t('members.daysAgo')}`
  if (days === 0) return t('members.today')
  return `${days} ${t('members.daysRemaining')}`
}

const getExpiryClass = (date) => {
  if (!date) return ''
  const days = dayjs(date).diff(dayjs(), 'day')
  if (days < 0) return 'expired'
  if (days <= 3) return 'critical'
  if (days <= 7) return 'warning'
  return 'normal'
}

const fetchMember = async () => {
  try {
    loading.value = true
    const response = await getMemberDetail(route.params.id)
    member.value = response.member
    payments.value = response.payments || []
  } catch (error) {
    console.error('Failed to fetch member:', error)
    member.value = null
  } finally {
    loading.value = false
  }
}

const fetchPlans = async () => {
  try {
    const response = await listPlans()
    plans.value = (response.plans || []).filter(p => p.is_active)
  } catch (error) {
    console.error('Failed to fetch plans:', error)
  }
}

const handleRenewal = async () => {
  try {
    renewalLoading.value = true
    await createPayment({
      member_id: member.value.id,
      plan_id: renewalData.value.plan_id,
      amount: renewalData.value.amount,
      payment_mode: renewalData.value.payment_mode,
    })
    showRenewalModal.value = false
    await fetchMember()
  } catch (error) {
    console.error('Failed to renew membership:', error)
  } finally {
    renewalLoading.value = false
  }
}

const handleSelectMember = (member) => {
  router.push(`/members/${member.id}`)
}

const handleLogout = async () => {
  await logout()
  router.push('/login')
}

// Print modal methods
const openPrintModal = (payment) => {
  printingPayment.value = payment
  showPrintModal.value = true
}

const handlePrint = () => {
  document.body.classList.add('printing-receipt')
  window.print()
  document.body.classList.remove('printing-receipt')
}

const handleToggleActive = async () => {
  if (!member.value) return
  const wasActive = member.value.is_active
  try {
    statusLoading.value = true
    await updateMember(member.value.id, { is_active: !wasActive })
    showStatusModal.value = false
    await fetchMember()
    toast.success(wasActive ? t('memberDetail.markedInactive') : t('memberDetail.reactivated'))
  } catch (error) {
    console.error('Failed to toggle member status:', error)
    toast.error(error?.response?.data?.error || t('memberDetail.statusUpdateFailed'))
  } finally {
    statusLoading.value = false
  }
}

const handleResendReceipt = async (payment) => {
  try {
    await resendReceipt(payment.id)
    alert('Receipt sent successfully')
  } catch (error) {
    console.error('Failed to resend receipt:', error)
    alert(error.message || 'Failed to send receipt')
  }
}

// Watch for route param changes to refetch member when navigating via search
watch(
  () => route.params.id,
  (newId, oldId) => {
    if (newId && newId !== oldId) {
      fetchMember()
    }
  },
  { immediate: true }
)

// Watch for plan selection to update amount
watch(() => renewalData.value.plan_id, (planId) => {
  if (planId) {
    const plan = plans.value.find(p => p.id === planId)
    if (plan) {
      renewalData.value.amount = plan.price
    }
  }
})

onMounted(() => {
  fetchPlans()
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';

.member-detail-view {
  padding: var(--space-6);
  max-width: 1400px;
}

.loading-state {
  display: flex;
  justify-content: center;
  padding: var(--space-12);
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-6);
  flex-wrap: wrap;
  gap: var(--space-4);
}

.header-left {
  display: flex;
  align-items: center;
  gap: var(--space-4);
}

.header-info {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  margin: 0;
}

.member-number {
  font-family: var(--font-mono);
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
}

.header-actions {
  display: flex;
  gap: var(--space-3);
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: var(--space-4);
  margin-bottom: var(--space-6);
}

.info-card {
  padding: var(--space-5);
}

.card-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
  margin: 0 0 var(--space-4) 0;
}

.membership-status {
  margin-bottom: var(--space-4);
}

.status-badge {
  display: inline-block;
  padding: var(--space-1) var(--space-3);
  border-radius: var(--radius-full);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
}

.status-badge.active {
  background: var(--color-success-bg);
  color: var(--color-success);
}

.status-badge.expiring {
  background: var(--color-warning-bg);
  color: var(--color-warning);
}

.status-badge.expired {
  background: var(--color-error-bg);
  color: var(--color-error);
}

.status-badge.inactive {
  background: var(--color-gray-100);
  color: var(--color-gray-500);
}

.info-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.info-label {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
}

.info-value {
  font-size: var(--font-size-sm);
  color: var(--color-charcoal);
  text-align: right;
}

.info-value.expiry.expired,
.info-value.expiry.critical {
  color: var(--color-error);
}

.info-value.expiry.warning {
  color: var(--color-warning);
}

.info-value.expiry.normal {
  color: var(--color-success);
}

.payment-history-card {
  padding: var(--space-5);
}

.section-header {
  margin-bottom: var(--space-4);
}

.section-header h3 {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
  margin: 0;
}

.empty-state {
  text-align: center;
  padding: var(--space-8);
  color: var(--color-gray-400);
}

.payments-table {
  width: 100%;
  border-collapse: collapse;
}

.payments-table th {
  text-align: left;
  padding: var(--space-3) var(--space-4);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-500);
  border-bottom: 1px solid var(--color-gray-200);
  background: var(--color-gray-50);
}

.payments-table td {
  padding: var(--space-3) var(--space-4);
  border-bottom: 1px solid var(--color-gray-100);
}

.payments-table tr.voided {
  opacity: 0.6;
  text-decoration: line-through;
}

.receipt-number {
  font-family: var(--font-mono);
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
}

.amount {
  font-weight: var(--font-weight-medium);
  color: var(--color-success);
}

.payment-status {
  display: inline-block;
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-full);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
}

.payment-status.active {
  background: var(--color-success-bg);
  color: var(--color-success);
}

.payment-status.void {
  background: var(--color-error-bg);
  color: var(--color-error);
}

.not-found {
  text-align: center;
  padding: var(--space-12);
}

.renewal-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.form-field label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-charcoal);
}

.form-field select {
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-gray-300);
  border-radius: var(--radius-md);
  font-size: var(--font-size-base);
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-3);
  margin-top: var(--space-4);
}

.danger-ghost {
  color: var(--color-error);
}

.danger-ghost:hover:not(:disabled) {
  background: var(--color-error-bg);
  color: var(--color-error);
}

.status-modal-content {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.status-modal-body {
  font-size: var(--font-size-sm);
  color: var(--color-charcoal);
  line-height: 1.5;
  margin: 0;
}

.actions {
  white-space: nowrap;
}

/* Print Modal */
.print-modal-content {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.print-preview-container {
  max-height: 70vh;
  overflow-y: auto;
  background: var(--color-gray-100);
  padding: var(--space-4);
  border-radius: var(--radius-md);
}

.print-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-3);
  padding-top: var(--space-4);
  border-top: 1px solid var(--color-gray-200);
}

@media (max-width: 768px) {
  .member-detail-view {
    padding: var(--space-4);
  }

  .page-header {
    flex-direction: column;
    align-items: stretch;
  }

  .header-actions {
    flex-direction: column;
  }
}

/* Print styles - hide everything except receipt when printing */
@media print {
  .member-detail-view,
  .page-header,
  .info-grid,
  .payment-history-card,
  .print-actions {
    display: none !important;
  }

  .print-preview-container {
    background: white !important;
    padding: 0 !important;
    max-height: none !important;
    overflow: visible !important;
  }
}
</style>
